import React, { useState } from 'react'
import { Drawer, Backdrop } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import { useRouteMatch } from 'react-router-dom'
import { useFormContext, useWatch } from 'react-hook-form'
import { ArrowForwardRounded, ArrowDownwardRounded, ExpandLessRounded, CloseRounded } from '@material-ui/icons'

import { useSelectors } from '../../../../store'
import { PriceText } from '../../../../lib/price-text'
import { ServicesSummary } from '../services-summary'
import { useBookingBar } from '../booking-bar-context'
import { AnalyticsService } from '../../../../services/analytics'

import { styles } from './styles'
import { money } from '../../../../lib/gbp'
import { fields } from '../../../../store/constants'
import { barTarget, PAY_BUTTONS_END } from '../../../../lib/booking-bar-target'
import { embeddedCheckout, platformOf, walletAddressFromSheet } from '../../../../lib/checkout-mode'
import { formVersion } from '../../../../lib/form-version'
import { barStatusText, platformWallet } from '../../../../lib/unpaid-status'
import { surchargeText } from '../../../../lib/price-lines'
import { useSurchargeLines } from '../../../../lib/use-surcharge-lines'

// Once per module: makeStyles called inside a render built a new JSS sheet,
// with new class names, on every render (booking screen, 11 Oct 2026).
const useStyles = makeStyles(styles)

const STEP_FORM_ID = 'booking-step-form'
const PAY_SECTION = '[data-testid="book-pay-section"]'
// Book & pay: the bar never goes while the booking is unpaid (17 Oct 2026).
// It used to hide while the pay buttons were on screen and, once the form
// was complete, below the details; in replay 01a1497c it vanished the moment
// AutoFill completed the form, with nothing in its place, and the customer
// left thinking she had booked. Now it stays, and says "Not booked yet"
// (lib/unpaid-status.js barStatusText). The page keeps room for it at the
// bottom (pages/booking/styles.js bookingShell), so nothing is out of reach.
// The wallet the pay area leads with (lib/checkout-mode.js), for the line.
const barWallet = () => (embeddedCheckout() ? platformWallet(platformOf()) : undefined)

export const MobileBookingBar = React.memo(() => {
  const classes = useStyles()
  const { canProceed } = useBookingBar()
  const [sheetOpen, setSheetOpen] = useState(false)
  const step = useRouteMatch('/booking/:step')?.params?.step
  const { baseService, price, prices } = useSelectors()
  const bookingForm = useFormContext()

  // Before /api/v2/services answers no base service can be resolved from the
  // URL yet, so a wash *was* picked but we cannot name or price it: show a
  // skeleton rather than "Pick your wash".
  const pricesLoaded = prices.ids.length > 0
  const primary = pricesLoaded ? baseService?.name || 'Pick your wash' : 'Your wash'
  const secondary = pricesLoaded ? (baseService ? money(price) : 'Tap a wash to get started') : <PriceText value={null} />
  // "Large car +£20 · Same day +£25" under the total (lib/price-lines.js).
  const surcharges = surchargeText(useSurchargeLines())

  // Book & pay: on a phone the pay section is three screens below the top of
  // the form, and in the 14 days to 7 Oct not one of 26 phone visitors who
  // left the screen ever touched it (docs/MOBILE_BOOK_DROP_2026-10-07.md).
  // So on this step the bar shows the total and a button that takes them to
  // the next thing to do, and says what it is: "Choose size", "Pick a day",
  // "Add your details", then "Pay £95" (lib/booking-bar-target.js). It does
  // not pay: the real pay buttons do.
  const isBook = step === 'book'
  // The one Car location box (lib/car-location.js) and what a pick filled.
  const watchedNames = [fields.carSize, fields.date, fields.postcode, fields.address, fields.mobile, fields.carLocation, fields.addressPicked]
  const watchedValues = useWatch({ control: bookingForm?.control, name: watchedNames }) || []
  const values = watchedNames.reduce((acc, name, i) => ({ ...acc, [name]: watchedValues[i] }), {})
  // Form v3's order, unless the wallet sheet gives the address.
  const v3 = formVersion() === 'v3' && !walletAddressFromSheet()
  const next = barTarget(values, pricesLoaded && baseService ? money(price) : '', v3)
  const [wallet] = useState(barWallet)

  // The CTA on the checkout step is "Place order", handled by step-checkout's
  // own button (it has Stripe-aware UX). Skip the bar there to avoid two CTAs.
  if (step === 'checkout' || step === 'finish' || step === 'success' || step === 'sign_in') return null

  const goToNext = () => {
    const now = barTarget(bookingForm ? bookingForm.getValues() : {}, '', v3)
    AnalyticsService.trackMobileBarBookTapped(now.target)
    // An empty car size or day is said on the field, not only scrolled to.
    if (now.target === 'car_size' && bookingForm) bookingForm.trigger(fields.carSize)
    if (now.target === 'date' && bookingForm) bookingForm.trigger(fields.date)
    const el = document.querySelector(now.selector) || document.querySelector(PAY_SECTION)
    if (!el) return
    const rect = el.getBoundingClientRect()
    // Clear of the sticky header at the top, and of this bar at the bottom.
    let top
    if (now.block === 'start') top = rect.top + window.scrollY - 84
    else if (now.block === 'end') top = rect.bottom + window.scrollY - window.innerHeight + 24
    else top = rect.top + window.scrollY - (window.innerHeight - rect.height) / 2
    try { window.scrollTo({ top, behavior: 'smooth' }) } catch (e) { window.scrollTo(0, top) }
    // The first empty detail box takes the keyboard (no extra scroll: the
    // page is already on its way there).
    const input = now.focusId && document.getElementById(now.focusId)
    if (input) {
      try { input.focus({ preventScroll: true }) } catch (e) { input.focus() }
    }
  }

  return (
    <>
      <div className={classes.bar}>
        {isBook && (
          <p className={classes.barStatus} role="status" data-testid="mobile-bar-status">
            {barStatusText({ target: next.target, wallet })}
          </p>
        )}
        <div className={classes.barRow}>
        <button
          type="button"
          className={classes.summaryChip}
          onClick={() => setSheetOpen(true)}
          aria-label="View order summary">
          <span className={classes.summaryText}>
            <span className={classes.summaryPrimary}>{primary}</span>
            <span className={classes.summarySecondary}>{secondary}</span>
            {pricesLoaded && baseService && surcharges && (
              <span className={classes.summarySurcharges} data-testid="mobile-bar-surcharges">{surcharges}</span>
            )}
          </span>
          <ExpandLessRounded className={classes.summaryChevron} />
        </button>
        {/* Always tappable - NOT disabled. When the step isn't complete it
            submits the form, which surfaces the missing fields (instead of a
            dead button). Dimmed to signal "finish the form first". */}
        {isBook ? (
          <button type="button" className={classes.nextButton} onClick={goToNext} data-testid="mobile-bar-book" data-target={next.target}>
            {next.label}
            <ArrowDownwardRounded style={{ fontSize: 18, marginLeft: 6 }} />
          </button>
        ) : (
          <button
            type="submit"
            form={STEP_FORM_ID}
            className={clsx(classes.nextButton, !canProceed && classes.nextButtonPending)}>
            Next
            <ArrowForwardRounded style={{ fontSize: 18, marginLeft: 6 }} />
          </button>
        )}
        </div>
      </div>

      <Drawer
        anchor="bottom"
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        PaperProps={{ className: classes.sheet }}
        BackdropComponent={Backdrop}>
        <div className={classes.sheetHandle} />
        <div className={classes.sheetHeader}>
          <span className={classes.sheetTitle}>Order summary</span>
          <button
            type="button"
            className={classes.sheetClose}
            onClick={() => setSheetOpen(false)}
            aria-label="Close">
            <CloseRounded fontSize="small" />
          </button>
        </div>
        <div className={classes.sheetBody}>
          <ServicesSummary sidebar embedded />
        </div>
      </Drawer>
    </>
  )
})

export { STEP_FORM_ID }
